package com.arena_web.ui.components.overlay

import com.arena_web.data.dto.WalletCheckoutRequest
import com.arena_web.data.dto.WalletConfirmRequest
import com.arena_web.domain.repositories.WalletRepository
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

// =============================================================================
// plata by mono (monobank acquiring) checkout. There is nothing to embed:
// the backend creates a hosted-page invoice and we redirect the whole tab
// to it. mono sends the payer back to the app afterwards, where the
// pending order saved in localStorage is confirmed (pull) — the webhook
// credits server-side regardless, the pull only makes the balance and the
// toast immediate.
// =============================================================================

/**
 * Starts a coin purchase: creates the pending order + mono invoice on the
 * backend, remembers it in localStorage and redirects to the payment page.
 * On success the current page unloads — there is no "approved" callback;
 * the result is handled by [resumePendingTopUp] after the redirect back.
 * [onFailed] fires only when checkout itself fails.
 */
fun startMonoPurchase(
    scope: CoroutineScope,
    repository: WalletRepository,
    planId: String,
    onFailed: (String?) -> Unit,
) {
    scope.launch {
        val checkout =
            repository.checkout(WalletCheckoutRequest(planId)).getOrElse { e ->
                onFailed(e.message)
                return@launch
            }
        val pageUrl = checkout.pageUrl
        if (pageUrl.isNullOrBlank()) {
            onFailed(null)
            return@launch
        }
        Storage.save(StorageKeys.WALLET_PENDING_ORDER, checkout.orderReference)
        Storage.saveInt(StorageKeys.WALLET_PENDING_COINS, checkout.coins)
        window.location.href = pageUrl
    }
}

/** Outcome of resuming the pending order after the redirect back from mono. */
sealed class PendingTopUpResult {
    /** Payment settled; coins are on the balance. */
    data class Credited(val coins: Int) : PendingTopUpResult()

    /** Payment failed on mono's side. */
    data object Failed : PendingTopUpResult()
}

/**
 * Confirms the pending top-up order left in localStorage, if any. Called once
 * on app load — that covers both the redirect back from the mono payment page
 * and a tab the user closed mid-payment and reopened later.
 *
 * The order is pull-confirmed with a few retries while the invoice is still
 * settling. A still-unpaid order (user backed out of the payment page) clears
 * the marker silently — if it does get paid later, the webhook credits it and
 * the next wallet refresh shows the balance. Returns null when there is
 * nothing to report.
 */
suspend fun resumePendingTopUp(repository: WalletRepository): PendingTopUpResult? {
    val orderReference = Storage.get(StorageKeys.WALLET_PENDING_ORDER) ?: return null
    val coins = Storage.getInt(StorageKeys.WALLET_PENDING_COINS)

    var status: String? = null
    for (attempt in 0 until 3) {
        if (attempt > 0) delay(1500)
        val confirmed = repository.confirmCheckout(WalletConfirmRequest(orderReference)).getOrNull()
        if (confirmed != null) {
            status = confirmed.orderStatus
            if (status != "PENDING") break
        }
    }

    // Unknown order / network failure: keep the marker for the next load
    // unless the backend explicitly told us the state.
    if (status == null) return null

    Storage.remove(StorageKeys.WALLET_PENDING_ORDER)
    Storage.remove(StorageKeys.WALLET_PENDING_COINS)
    return when (status) {
        "PAID" -> PendingTopUpResult.Credited(coins)
        "DECLINED", "REFUNDED" -> PendingTopUpResult.Failed
        else -> null // still PENDING — user backed out; webhook settles it if paid
    }
}
